// The ad page's data from the public API, as the legacy Vehicle model. Older
// links carry a legacy id that differs from ad.id: when byAdId finds nothing
// the id is resolved through byIterationId and the address bar is rewritten
// to the ad.id URL, as web-ui-v2 does.

import { useEffect, useState } from 'react';
import { Vehicle } from '../../@types/codegen/types';
import { Tree } from '../../@types/vehicle-properties';
import { gqlQuery } from '../client';
import { PublicApiSettings } from '../config';
import { toPropertyTree, toVehicle } from './adapter';
import { AD_ID_BY_LEGACY_ID_QUERY, AD_QUERY } from './queries';
import { Ad, AdData, AdIdByLegacyIdData } from './types';

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export interface AdResult {
  ad: Ad;
  vehicle: Vehicle;
  propertyTree: Tree;
}

const fetchAd = async (publicApi: PublicApiSettings, id: string): Promise<Ad | null> => {
  const data = await gqlQuery<AdData>(publicApi.api, AD_QUERY, { id });
  return data.ads?.byAdId || null;
};

const resolveLegacyId = async (publicApi: PublicApiSettings, id: string) => {
  if (!UUID.test(id)) return null;
  try {
    const data = await gqlQuery<AdIdByLegacyIdData>(publicApi.api, AD_ID_BY_LEGACY_ID_QUERY, {
      id,
    });
    return data.ads?.byIterationId?.ads?.[0]?.id || null;
  } catch (_e) {
    return null;
  }
};

const useAd = (id: string | undefined, publicApi: PublicApiSettings | undefined) => {
  const [result, setResult] = useState<AdResult>();
  const [loading, setLoading] = useState(!!publicApi && !!id);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (!publicApi || !id) return;
    let active = true;
    setLoading(true);
    setError(false);
    (async () => {
      let ad = await fetchAd(publicApi, id);
      if (!ad) {
        const adId = await resolveLegacyId(publicApi, id);
        if (adId && adId !== id) {
          ad = await fetchAd(publicApi, adId);
          if (ad && window.location.pathname.includes(id)) {
            history.replaceState(
              undefined,
              '',
              `${window.location.pathname.replace(id, adId)}${window.location.search}${window.location.hash}`
            );
          }
        }
      }
      if (!active) return;
      setResult(ad ? { ad, vehicle: toVehicle(ad), propertyTree: toPropertyTree(ad) } : undefined);
    })()
      .catch(() => active && setError(true))
      .finally(() => active && setLoading(false));
    return () => {
      active = false;
    };
  }, [id, publicApi?.api.url, publicApi?.branchGroupId]);

  return { loading, error, result };
};

export default useAd;
